import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from "react";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutFormInputs from "./checkoutFormInputs";
import i18next from "i18next";
import LoadingCheckout from "../details/reservation/loadingCheckout";
import Alert from "../commons/alert";
import * as Sentry from "@sentry/react";
import { getStripePromise } from "../../../infrastructure/stripe/stripeLoader";
const CheckoutForm = ({ myData, formData, checkoutId, setOperationEnded, clientSecret, setClientSecret, setLoadingCheckout, loadingCheckout, email, paymentType, checkSubmit, setCheckSubmit, userUFP, localCurrency, onlyCreditCard, localValue, localCurrencyVisible, ip, setPaymentData, onlyOnDestination, name, generateCheckout, checkoutAmount, checkoutOfferId, babySeatSelection, esimSelection }) => {
    var _a, _b, _c, _d, _e;
    const [isValidForm, setIsValidForm] = useState(false);
    const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
    const stripePromise = useMemo(() => getStripePromise(), []);
    const shouldUseLocalCurrency = localCurrency && localCurrencyVisible;
    const baseAmount = (_e = (_d = (_c = (_b = (_a = myData.pricesDetail) === null || _a === void 0 ? void 0 : _a.currency) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.total) === null || _d === void 0 ? void 0 : _d.baseAmount) !== null && _e !== void 0 ? _e : 0;
    const effectiveAmount = shouldUseLocalCurrency ? localValue : (checkoutAmount !== null && checkoutAmount !== void 0 ? checkoutAmount : baseAmount);
    const amountWithoutDecimal = parseInt(effectiveAmount.toString());
    const effectiveCurrency = shouldUseLocalCurrency ? formData.currency : "USD";
    const effectiveName = (name === null || name === void 0 ? void 0 : name.trim()) ? name : "default user name";
    const stripeName = (name === null || name === void 0 ? void 0 : name.trim()) ? name : sessionStorage.getItem("name") || "default user name";
    console.log("CHECKOUT OFFER ID", myData.id);
    const payload = useMemo(() => ({
        idSession: formData.idSession,
        checkoutId: checkoutId,
        amount: effectiveAmount,
        currency: effectiveCurrency,
        name: effectiveName,
        description: myData.car.name,
        offerId: checkoutOfferId !== null && checkoutOfferId !== void 0 ? checkoutOfferId : myData.id,
        email: email,
        paymentType: paymentType,
        visitorId: userUFP != null ? userUFP : sessionStorage.getItem("userFP"),
        isActiveLocalCurrency: localCurrencyVisible,
        paymentMethodTypes: ["card"],
        automaticPaymentMethods: { enabled: false },
        ...(babySeatSelection ? { babySeatSelection } : {}),
        ...(esimSelection ? { esimSelection } : {}),
    }), [
        effectiveAmount,
        effectiveCurrency,
        effectiveName,
        checkoutId,
        email,
        formData.idSession,
        localCurrencyVisible,
        myData.car.name,
        myData.id,
        paymentType,
        userUFP,
        babySeatSelection,
        esimSelection,
    ]);
    useEffect(() => {
        // Set the session variable when the component mounts
        sessionStorage.setItem("onCheckout", "yes");
        // Cleanup function to set onCheckout to "no" only if it's not a reload
        return () => {
            if (window.performance.navigation.type !== window.performance.navigation.TYPE_RELOAD) {
                sessionStorage.setItem("onCheckout", "no");
            }
        };
    }, []);
    // Stripe expects base language codes (e.g. "en", "es", "pt"), not regional
    // variants like "en-GB". Passing an unsupported locale causes Elements to
    // fail silently, leaving the payment form blank.
    const locale = i18next.language.split("-")[0];
    const appearance = useMemo(() => ({
        theme: "stripe",
        variables: {
            colorBackground: "#ffffff",
            colorText: "#000",
            colorDanger: "#EC0101",
            fontFamily: "Open Sans, sans-serif",
            spacingUnit: ".25rem",
            borderRadius: ".25rem",
        },
        rules: {
            ".Label": {
                fontSize: "0",
                marginBottom: "0",
            },
            ".Input": {
                boxShadow: "none",
                fontSize: "16px",
                borderColor: "#EAEAEA",
            },
            ".Input:focus": {
                boxShadow: "none",
                borderColor: "#cbcbcb",
            },
            ".Error": {
                fontSize: "0",
            },
        },
    }), []);
    const options = useMemo(() => ({
        amount: amountWithoutDecimal * 100,
        currency: localCurrency && localCurrencyVisible ? formData.currency.toLowerCase() : "usd",
        name: stripeName,
        mode: "payment",
        // Explicitly list payment methods to avoid automatic payment methods.
        // Must match PaymentIntents created with payment_method_types.
        //paymentMethodTypes: ["card"],
        appearance,
        locale: locale,
        autoFill: true,
    }), [
        amountWithoutDecimal,
        appearance,
        formData.currency,
        localCurrency,
        localCurrencyVisible,
        locale,
    ]);
    const alertStyles = {
        base: {
            border: "1px solid #dcdcdc", // $color-gray-4
            borderRadius: "4px", // map-get($spacers, xs)
            backgroundColor: "rgba(220, 220, 220, 0.06)", // $color-gray-3 con alpha
            padding: "8px 4px", // xs + xxs
        },
        secondary: {
            backgroundColor: "rgba(0, 123, 255, 0.06)", // ejemplo color secundario
            borderColor: "#007bff", // $color-secondary
        },
        transaction: {
            marginTop: "12px", // map-get($spacers, s)
        },
        content: {
            fontFamily: '"Open Sans", sans-serif',
            fontSize: "1rem",
            color: "#000000",
            margin: 0,
        },
    };
    const generalErrorFallback = _jsx(_Fragment, {}); // Do not show user-facing fallback; errors go to Sentry/console
    useEffect(() => {
        let cancelled = false;
        stripePromise
            .then((stripe) => {
            if (cancelled || stripe) {
                return;
            }
            setStripeLoadFailed(true);
            Sentry.captureMessage("Stripe checkout failed to load", {
                level: "error",
                tags: { component: "CheckoutForm", reason: "stripe-promise-null" },
            });
        })
            .catch((error) => {
            if (cancelled) {
                return;
            }
            setStripeLoadFailed(true);
            Sentry.captureException(error, {
                tags: { component: "CheckoutForm", context: "stripe-promise" },
            });
        });
        return () => {
            cancelled = true;
        };
    }, [stripePromise]);
    return (_jsx(Sentry.ErrorBoundary, { fallback: generalErrorFallback, onError: (error) => console.error("Checkout render error captured:", error), children: _jsxs(_Fragment, { children: [_jsx("div", { style: loadingCheckout && isValidForm ? { display: "block" } : { display: "none" }, children: _jsx(LoadingCheckout, {}) }), _jsxs("div", { style: (loadingCheckout && !isValidForm) || !loadingCheckout ? { display: "block" } : { display: "none" }, children: [_jsx(Elements, { options: options, stripe: stripePromise, children: _jsx(CheckoutFormInputs, { myData: myData, formData: formData, setLoadingCheckout: setLoadingCheckout, loadingCheckout: loadingCheckout, clientSecret: clientSecret, payload: payload, setClientSecret: setClientSecret, amountWithoutDecimal: amountWithoutDecimal, checkSubmit: checkSubmit, setCheckSubmit: setCheckSubmit, localCurrencyVisible: localCurrencyVisible, localCurrency: localCurrency, ip: ip, isValidForm: isValidForm, setIsValidForm: setIsValidForm, onlyCreditCard: onlyCreditCard, setPaymentData: setPaymentData, generateCheckout: generateCheckout, onStripeReady: () => setStripeLoadFailed(false), onStripeLoadError: () => setStripeLoadFailed(true) }) }), onlyCreditCard && onlyOnDestination && (_jsx(Alert, { type: "secondary", customClass: "transaction", style: {
                                ...alertStyles.base,
                                ...alertStyles.secondary,
                                ...alertStyles.transaction
                            }, children: i18next.t("checkout-form__only-on-destination") }))] })] }) }));
};
export default CheckoutForm;
